// Client for the self-hosted DB backend (db-service/, FastAPI + Postgres).
//
// Supabase is used for AUTH ONLY. All application data (clients, reports,
// per-step report JSON, page inclusions, webhook logs, user activity logs)
// lives in the self-hosted Postgres and goes through this client instead of
// the Supabase JS client.
//
// Every request carries the caller's own live Supabase session token (the
// same one supabase.auth.getSession() returns), not a shared secret - the
// backend verifies it's a genuine, unexpired Supabase-issued token for this
// project (see db-service/app/auth.py). That's real per-user authentication,
// safe to expose to real users, unlike a static bearer token baked into the
// build.

import { supabase } from './supabaseClient';

// Falls back to the real production API when REACT_APP_DB_API_URL isn't set -
// same pattern as the other REACT_APP_* defaults in this codebase (e.g.
// supabaseClient.ts). This matters because the production Docker build's
// .dockerignore excludes .env from the build context entirely, so the
// deployed bundle always gets this fallback rather than any .env value;
// local dev overrides it via the repo root .env (REACT_APP_DB_API_URL=
// http://localhost:4000), which is loaded directly by `npm start`/CRA and
// takes precedence over this default.
const API_URL = process.env.REACT_APP_DB_API_URL || 'https://db.chimneysweeps.com';

export class DbApiError extends Error {
  status: number;
  body: any;
  constructor(message: string, status: number, body: any) {
    super(message);
    this.name = 'DbApiError';
    this.status = status;
    this.body = body;
  }
}

async function request<T = any>(
  path: string,
  options: { method?: string; body?: any; query?: Record<string, any> } = {}
): Promise<T> {
  const { method = 'GET', body, query } = options;

  let url = `${API_URL}${path}`;
  if (query) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) params.set(key, String(value));
    });
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token || '';

  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as unknown as T;

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const message = (isJson && payload?.detail) || (isJson && payload?.error) || res.statusText;
    throw new DbApiError(message, res.status, payload);
  }

  return payload as T;
}

// ---------- Clients ----------
export const clientsApi = {
  // full_name: exact match (find-or-create lookups). search: partial/paginated, for browsing UI.
  list: (params?: { full_name?: string; search?: string; limit?: number; offset?: number }) =>
    request<{ data: any[]; total?: number; limit?: number; offset?: number }>('/api/clients', { query: params }),
  get: (id: string) => request<{ data: any }>(`/api/clients/${id}`),
  create: (body: { full_name?: string; email?: string; phone?: string; created_by?: string }) =>
    request<{ data: any }>('/api/clients', { method: 'POST', body }),
};

// ---------- Reports ----------
export const reportsApi = {
  list: (params?: {
    client_id?: string;
    client_ids?: string;
    client_name?: string;
    source?: string;
    manual_only?: boolean;
    webhook_only?: boolean;
    created_after?: string;
    created_before?: string;
    limit?: number;
    offset?: number;
  }) => request<{ data: any[]; total?: number }>('/api/reports', { query: params }),
  get: (id: string) => request<{ data: any }>(`/api/reports/${id}`),
  create: (body: { client_name?: string; created_by?: string; client_id?: string; source?: string; webhook_log_id?: string }) =>
    request<{ data: any }>('/api/reports', { method: 'POST', body }),
  patch: (id: string, body: Record<string, any>) =>
    request<{ data: any }>(`/api/reports/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => request<void>(`/api/reports/${id}`, { method: 'DELETE' }),
};

// ---------- Step data (step1_json, step3_json, step5_invoice_json, step5_part2_json, step6_json, step7_json, step8_json) ----------
export type StepTable =
  | 'step1_json'
  | 'step3_json'
  | 'step5_invoice_json'
  | 'step5_part2_json'
  | 'step6_json'
  | 'step7_json'
  | 'step8_json';

export const stepsApi = {
  get: (table: StepTable, reportId: string) => request<{ data: any }>(`/api/steps/${table}/${reportId}`),
  upsert: (table: StepTable, reportId: string, data: any) =>
    request<{ data: any }>(`/api/steps/${table}/${reportId}`, { method: 'PUT', body: { data } }),
  // Omit reportIds (or pass undefined) to fetch every row in the table.
  batchGet: (table: StepTable, reportIds?: string[]) =>
    request<{ data: any[] }>(`/api/steps/${table}`, {
      query: reportIds ? { report_ids: reportIds.join(',') } : undefined,
    }),
};

// ---------- Page inclusions ----------
export const pageInclusionsApi = {
  list: (reportId: string) => request<{ data: number[] }>(`/api/reports/${reportId}/page-inclusions`),
  include: (reportId: string, pageNumber: number) =>
    request<void>(`/api/reports/${reportId}/page-inclusions/${pageNumber}`, { method: 'PUT' }),
  exclude: (reportId: string, pageNumber: number) =>
    request<void>(`/api/reports/${reportId}/page-inclusions/${pageNumber}`, { method: 'DELETE' }),
  bulkSet: (reportId: string, pages: number[]) =>
    request<void>(`/api/reports/${reportId}/page-inclusions`, { method: 'PUT', body: { pages } }),
};

// ---------- Webhook logs ----------
export const webhookLogsApi = {
  list: (params?: { viewed?: boolean; limit?: number; offset?: number }) =>
    request<{ data: any[]; total: number; limit: number; offset: number }>('/api/webhook-logs', { query: params }),
  get: (id: string) => request<{ data: any }>(`/api/webhook-logs/${id}`),
  create: (body: { payload: any; headers?: string; ip_address?: string; status?: string }) =>
    request<{ data: any }>('/api/webhook-logs', { method: 'POST', body }),
  patch: (id: string, body: { viewed?: boolean; status?: string; payload?: any }) =>
    request<{ data: any }>(`/api/webhook-logs/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => request<void>(`/api/webhook-logs/${id}`, { method: 'DELETE' }),
  removeAll: () => request<void>('/api/webhook-logs', { method: 'DELETE' }),
};

// ---------- User activity logs ----------
export const userLogsApi = {
  create: (body: Record<string, any>) => request<{ data?: any; error?: string }>('/api/user-logs', { method: 'POST', body }),
  list: (params?: { event_category?: string; username?: string; search?: string; page?: number; per_page?: number }) =>
    request<{ data: any[]; total: number; page: number; per_page: number }>('/api/user-logs', { query: params }),
  stats: (username: string, days?: number) =>
    request<{ data: any }>('/api/user-logs/stats', { query: { username, days } }),
};

// ---------- Users ----------
export const usersApi = {
  list: () => request<{ data: any[] }>('/api/users'),
  upsert: (body: { email: string; username: string; full_name?: string }) =>
    request<{ data: any }>('/api/users', { method: 'POST', body }),
};
